import React from 'react';

import { CtaButton, NumberedIcon } from '@opendoor/bricks';
import { LISTING_OFFER_CHANNELS } from '../../constants/ListingOfferConstants';
import {
  IAssigneeBasic,
  IDocumentChecklistItem,
  IRequestStatus,
  IRequestStatusMap,
  ListingOfferChannel,
} from '../../declarations';
import FileUploadBox from './FileUploadBox';
import FileUploadRow from './FileUploadRow';

import './SellerResponseAcceptanceFlowBoxStyle.less';

export interface ISellerResponseAcceptanceFlowBoxProps {
  canGenerateSellerResponse: boolean;
  channel: ListingOfferChannel;
  creditAddendumItem?: IDocumentChecklistItem;
  generateSellerResponseRequestStatus?: IRequestStatus;
  onDocumentDownload: (id: number) => void;
  onDocumentRemove: (id: number) => void;
  onDocumentSave: (
    name: string,
    documentType: string,
    fileBase64DataUrl: string,
    requestId: number,
    checklistItemId?: number,
  ) => void;
  onRequestGenerateSellerResponse: () => void;
  removeChecklistDocumentRequests: IRequestStatusMap;
  resaleAgentLead: IAssigneeBasic;
  sellerResponseItem?: IDocumentChecklistItem;
  uploadChecklistDocumentRequests: IRequestStatusMap;
}

export default class SellerResponseAcceptanceFlowBox extends React.PureComponent<
  ISellerResponseAcceptanceFlowBoxProps
> {
  public renderContactHomeAdvisor = () => {
    const { resaleAgentLead } = this.props;

    return (
      <div>
        To negotiate the repair response,{' '}
        <a href={`mailto:${resaleAgentLead.external_email || resaleAgentLead.email}`}>
          email your Experience Partner.
        </a>
      </div>
    );
  };

  public renderGenerateResponse = () => {
    const {
      generateSellerResponseRequestStatus,
      onRequestGenerateSellerResponse,
      resaleAgentLead,
    } = this.props;

    const generateResponseIsLoading =
      generateSellerResponseRequestStatus && generateSellerResponseRequestStatus.isLoading;
    const generateResponseError =
      generateSellerResponseRequestStatus && generateSellerResponseRequestStatus.error;

    return (
      <div className="row">
        {generateResponseError ? (
          <div className="col-lg-7 col-xs-12">
            Oops. Looks like we ran into an issue generating your document. You can try again, or{' '}
            <a href={`mailto:${resaleAgentLead.external_email || resaleAgentLead.email}`}>
              email your Experience Partner
            </a>{' '}
            for assistance.
          </div>
        ) : (
          <div className="col-lg-7 col-xs-12">
            To negotiate the repair response,{' '}
            <a href={`mailto:${resaleAgentLead.external_email || resaleAgentLead.email}`}>
              email your Experience Partner and request an adjustment.
            </a>
          </div>
        )}
        <div className="col-lg-4 col-lg-offset-1 col-xs-12 generate-response-button">
          <CtaButton
            actionText={generateResponseError ? 'Try again' : 'Accept and continue'}
            loading={generateResponseIsLoading}
            onClick={onRequestGenerateSellerResponse}
          />
        </div>
      </div>
    );
  };

  public renderPreSubmitHeader = () => {
    return (
      <div className="seller-response-pre-submit-header">
        Please return the signed final document.
      </div>
    );
  };

  public renderPreAccept = () => {
    const { canGenerateSellerResponse, generateSellerResponseRequestStatus } = this.props;
    const generateResponseError =
      generateSellerResponseRequestStatus && generateSellerResponseRequestStatus.error;

    return (
      <div>
        {!generateResponseError && this.renderPreSubmitHeader()}
        {canGenerateSellerResponse
          ? this.renderGenerateResponse()
          : this.renderContactHomeAdvisor()}
      </div>
    );
  };

  public renderInstructions = () => {
    const instructions = [
      <span key={1}>Download the seller's response document(s)</span>,
      <span key={2}>Collect buyer's signature</span>,
      <span key={3}>Upload the signed document(s)</span>,
    ];
    return (
      <div className="seller-response-submission-instructions">
        {instructions.map((item, idx) => (
          <div className="instruction" key={idx}>
            <NumberedIcon num={idx + 1} className="instruction-icon" />
            {item}
          </div>
        ))}
      </div>
    );
  };

  public renderTextPreDocumentUpload = () => {
    const { resaleAgentLead } = this.props;
    return (
      <div>
        {this.renderPreSubmitHeader()}
        <div className="document-upload-message">
          Let's finalize the repair response. If your buyer would like to request changes,{' '}
          <a href={`mailto:${resaleAgentLead.external_email || resaleAgentLead.email}`}>
            email your Experience Partner and request an adjustment.
          </a>
        </div>
        {this.renderInstructions()}
      </div>
    );
  };

  public renderTextWhilePending = () => {
    return (
      <div className="document-upload-message">
        Thanks for uploading the repair response. Our Experience Partner will review and have the
        seller sign the document. The file status will update accordingly.
      </div>
    );
  };

  public renderTextWhenApproved = () => {
    return (
      <div className="document-upload-message">
        Thanks for uploading the buyer-signed seller response document. Your Experience Partner will
        review the document, collect a signature from the seller, and create any necessary work
        orders on the home.
      </div>
    );
  };

  public renderUploadMessage = () => {
    const { creditAddendumItem, sellerResponseItem } = this.props;
    const presentItems = [creditAddendumItem, sellerResponseItem].filter(i => i);

    if (presentItems.some(item => FileUploadBox.shouldShowUploadButton(item))) {
      return this.renderTextPreDocumentUpload();
    }
    if (presentItems.some(item => FileUploadBox.isPending(item))) {
      return this.renderTextWhilePending();
    }
    if (presentItems.some(item => FileUploadBox.isApproved(item))) {
      return this.renderTextWhenApproved();
    }
  };

  public renderPostAccept = () => {
    const {
      creditAddendumItem,
      onDocumentDownload,
      onDocumentRemove,
      onDocumentSave,
      removeChecklistDocumentRequests,
      sellerResponseItem,
      uploadChecklistDocumentRequests,
    } = this.props;
    return (
      <div>
        {this.renderUploadMessage()}
        <FileUploadRow
          documentChecklistItem={sellerResponseItem}
          onDocumentDownload={onDocumentDownload}
          onDocumentRemove={onDocumentRemove}
          onDocumentSave={onDocumentSave}
          removeChecklistDocumentRequests={removeChecklistDocumentRequests}
          uploadChecklistDocumentRequests={uploadChecklistDocumentRequests}
        />
        {creditAddendumItem && (
          <FileUploadRow
            documentChecklistItem={creditAddendumItem}
            onDocumentDownload={onDocumentDownload}
            onDocumentRemove={onDocumentRemove}
            onDocumentSave={onDocumentSave}
            removeChecklistDocumentRequests={removeChecklistDocumentRequests}
            uploadChecklistDocumentRequests={uploadChecklistDocumentRequests}
          />
        )}
      </div>
    );
  };

  public hasAcceptedResponse = () => {
    const { sellerResponseItem } = this.props;
    return sellerResponseItem && sellerResponseItem.document;
  };

  public renderDirectBuyerSection = () => {
    const { resaleAgentLead } = this.props;

    return (
      <div>
        {!this.hasAcceptedResponse() && this.renderPreSubmitHeader()}
        <div>
          To discuss your repair request,{' '}
          <a href={`mailto:${resaleAgentLead.external_email || resaleAgentLead.email}`}>
            email your Experience Partner.
          </a>
        </div>
      </div>
    );
  };

  public render(): JSX.Element {
    const { channel } = this.props;
    const isDirectBuyer = channel === LISTING_OFFER_CHANNELS.direct;
    return (
      <div className="rcd-seller-response-acceptance-flow-box">
        {isDirectBuyer && this.renderDirectBuyerSection()}
        {!isDirectBuyer &&
          (this.hasAcceptedResponse() ? this.renderPostAccept() : this.renderPreAccept())}
      </div>
    );
  }
}
